import type { FunctionComponent } from 'react';
import { useCallback, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { TileControlGroup, TileStickyHeader } from '~/ui/core/tile/Tile';
import { LanguagePicker } from '~/ui/domain/language-picker/LanguagePicker';
import { ActionButtons, HeaderContainer, Seperator } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { useActiveLanguage } from '../providers/ActiveLanguageProvider';

export const CompareVersionHeader: FunctionComponent<{
    readonly onClose: () => void;
    /** Where a picked language goes besides the comparison itself, such as the tile's url. */
    readonly onLanguageChange?: (language: LanguageCodeForJSA) => void;
}> = ({ onClose, onLanguageChange }) => {
    const { translate } = useContext(ApplicationContext);
    const { activeLanguage, availableLanguages, setActiveLanguage } = useActiveLanguage();

    const handleLanguageChange = useCallback(
        (language: LanguageCodeForJSA) => {
            setActiveLanguage(language);
            onLanguageChange?.(language);
        },
        [onLanguageChange, setActiveLanguage],
    );

    return (
        <TileStickyHeader>
            <HeaderContainer>
                <ActionButtons>
                    <TileControlGroup>
                        <Button
                            aria-label={translate('common.close')}
                            iconOnly={true}
                            size="sm"
                            variant="secondary"
                            onClick={onClose}
                        >
                            <IconCrossSmall />
                        </Button>
                    </TileControlGroup>
                    <Seperator />
                    <LanguagePicker
                        activeText={translate(`common.languages.${activeLanguage}` as TranslationKey)}
                        disabled={false}
                        isGenerating={false}
                        languages={availableLanguages}
                        onLanguageChange={handleLanguageChange}
                    />
                </ActionButtons>
            </HeaderContainer>
        </TileStickyHeader>
    );
};
